
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { templatesAPI } from '../services/api.jsx';
import api from '../services/api.jsx';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from '../components/ProtectedRoute';
import EmailEditor from 'react-email-editor';
import ConfirmModal from '../components/ConfirmModal.jsx';
import TemplateLibrary from '../components/TemplateLibrary.jsx';
import TemplateCustomizer from '../components/TemplateCustomizer.jsx';
import TemplateImages from './TemplateImages.jsx';
import { getAiPresetForTemplate } from '../components/TemplateStylePreview.jsx';
import { formatEventDateTimeDisplay } from '../utils/eventDateTime.js';
import './Templates.css';

const RSC_LOGO_WHITE_URL = 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Logo_white_1024x600.png';
const RSC_LOGO_BLUE_URL = 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Base Logo_1024x600_NEW.png';
const RSC_LOGO_DEFAULT_WIDTH = 120;
const EMAIL_CONTENT_WIDTH = '600px';
const TEMPLATE_IMAGE_FALLBACK_URL = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500" viewBox="0 0 800 500"><rect width="800" height="500" fill="#e2e8f0"/><rect x="24" y="24" width="752" height="452" rx="16" fill="#f8fafc" stroke="#cbd5e1" stroke-width="3"/><g fill="#64748b" font-family="Arial, Helvetica, sans-serif" text-anchor="middle"><text x="400" y="235" font-size="34" font-weight="700">Template preview unavailable</text><text x="400" y="276" font-size="20">Upload thumbnail assets to CDN to replace this placeholder</text></g></svg>')}`;

const normalizeRowValues = (rowValues) => {
  const values = {
    ...(rowValues || {}),
    fullWidth: false
  };

  if (values.backgroundImage) {
    // Preserve Unlayer background image settings exactly as saved to avoid visual drift on reopen.
    values.backgroundImage = { ...values.backgroundImage };
  }

  return values;
};

const normalizeUnlayerDesign = (design) => {
  if (!design || typeof design !== 'object') {
    return design;
  }

  const clonedDesign = JSON.parse(JSON.stringify(design));
  const normalizedBody = clonedDesign.body || {};
  const normalizedRows = Array.isArray(normalizedBody.rows)
    ? normalizedBody.rows.map((row) => ({
        ...row,
        values: normalizeRowValues(row?.values)
      }))
    : [];

  return {
    ...clonedDesign,
    body: {
      ...normalizedBody,
      rows: normalizedRows,
      values: {
        backgroundColor: '#ffffff',
        ...(normalizedBody.values || {}),
        contentWidth: EMAIL_CONTENT_WIDTH
      }
    }
  };
};

const Templates = () => {
  const { user, hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const canViewAllTemplates = hasAnyPermission(['templates:read:any']);
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });
  const [showAIAssist, setShowAIAssist] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [ignoreTemplateContext, setIgnoreTemplateContext] = useState(false);
  const [aiFormData, setAiFormData] = useState({
    purpose: '',
    tone: 'professional',
    type: 'event-invite',
    details: '',
    designStyle: 'modern-corporate',
    layoutStyle: 'hero-cta',
    creativityLevel: 'balanced',
    usePexelsImages: false,
    styleProfile: null
  });
  const [eventContext, setEventContext] = useState(null);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showTemplateCustomizer, setShowTemplateCustomizer] = useState(false);
  const [selectedLibraryTemplate, setSelectedLibraryTemplate] = useState(null);
  const [libraryApplyBanner, setLibraryApplyBanner] = useState('');
  const [showUnlayerPicker, setShowUnlayerPicker] = useState(false);
  const [unlayerTemplates, setUnlayerTemplates] = useState([]);
  const [unlayerTemplateSource, setUnlayerTemplateSource] = useState('');
  const [unlayerPickerLoading, setUnlayerPickerLoading] = useState(false);
  const [unlayerPickerError, setUnlayerPickerError] = useState('');
  const [showManagedImagePicker, setShowManagedImagePicker] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibraryImages, setImageLibraryImages] = useState([]);
  const [imageLibraryLoading, setImageLibraryLoading] = useState(false);
  const [imageLibraryCopied, setImageLibraryCopied] = useState(null);
  const [imageLibraryVariants, setImageLibraryVariants] = useState({});
  const emailEditorRef = useRef(null);
  const lastEditedDesignItemRef = useRef(null);
  const managedImageInsertCallbackRef = useRef(null);
  const pendingBuilderHtmlRef = useRef('');
  const pendingOpenStarterRef = useRef(false);
  const createTemplateInitRef = useRef(false);
  const hasHydratedBuilderRef = useRef(false);
  const [builderReady, setBuilderReady] = useState(false);
  const [builderHtml, setBuilderHtml] = useState('');
  const [builderVersion, setBuilderVersion] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    description: '',
    isGlobal: false,
    _builderDesign: null
  });

  const normalizeDesign = (design) => normalizeUnlayerDesign(design);

  const formatEventDate = (value, eventTimeZone) => formatEventDateTimeDisplay(value, eventTimeZone);

  const exportBuilderHtml = () => new Promise((resolve) => {
    const editor = emailEditorRef.current?.editor;
    if (!editor) {
      resolve(formData.body || '');
      return;
    }

    editor.exportHtml(({ html, design }) => {
      const safeHtml = html || '';
      const safeDesign = normalizeDesign(design || null);
      setBuilderHtml(safeHtml);
      if (safeDesign) {
        setFormData((prev) => ({
          ...prev,
          _builderDesign: safeDesign
        }));
      }
      resolve(safeHtml);
    });
  });

  const getBuilderHtml = () => builderHtml || formData.body || '';

  const buildUnlayerDesignFromHtml = (content) => ({
    ...normalizeDesign({
      body: {
        id: `row-${Date.now()}`,
        rows: [
          {
            id: `section-${Date.now()}`,
            cells: [1],
            columns: [
              {
                id: `col-${Date.now()}`,
                contents: [
                  {
                    id: `html-${Date.now()}`,
                    type: 'html',
                    values: {
                      html: content || ''
                    }
                  }
                ]
              }
            ]
          }
        ]
      }
    })
  });

  const loadBuilderContent = (content) => {
    const editor = emailEditorRef.current?.editor;
    if (!editor) {
      pendingBuilderHtmlRef.current = content || '';
      return;
    }

    let design = null;
    try {
      if (typeof content === 'string' && content.trim().startsWith('{')) {
        design = JSON.parse(content);
      }
    } catch {
      design = null;
    }

    if (design && design?.body?.rows) {
      editor.loadDesign(normalizeDesign(design));
      setBuilderHtml(content || '');
    } else {
      editor.loadDesign(buildUnlayerDesignFromHtml(content || ''));
      setBuilderHtml(content || '');
    }
  };

  const uploadTemplateImage = async (file) => {
    const response = await templatesAPI.uploadImage(file);
    return response.data?.imageUrl;
  };

  const syncBuilderToBody = async () => {
    const html = await exportBuilderHtml();
    if (!html) return;
    setFormData((prev) => (prev.body === html ? prev : { ...prev, body: html }));
    setBuilderVersion((value) => value + 1);
  };

  const buildSingleColumnRow = (content, prefix = 'insert') => ({
    id: `${prefix}-row-${Date.now()}`,
    cells: [1],
    columns: [
      {
        id: `${prefix}-col-${Date.now()}`,
        contents: [content]
      }
    ]
  });

  const insertContentIntoDesign = (design, content, prefix = 'insert') => {
    const rows = Array.isArray(design?.body?.rows) ? [...design.body.rows] : [];
    const marker = lastEditedDesignItemRef.current;

    const createRow = () => buildSingleColumnRow(content, prefix);

    if (rows.length === 0) {
      return {
        ...design,
        body: {
          ...(design?.body || {}),
          rows: [createRow()]
        }
      };
    }

    if (marker?.id) {
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
        const row = rows[rowIndex];

        if (marker.type === 'row' && row?.id === marker.id) {
          rows.splice(rowIndex + 1, 0, createRow());
          return {
            ...design,
            body: {
              ...design.body,
              rows
            }
          };
        }

        const columns = Array.isArray(row?.columns) ? row.columns : [];
        for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
          const column = columns[columnIndex];
          const contents = Array.isArray(column?.contents) ? column.contents : [];

          if (marker.type === 'column' && column?.id === marker.id) {
            const nextContents = [...contents, content];
            const nextColumns = [...columns];
            nextColumns[columnIndex] = { ...column, contents: nextContents };
            rows[rowIndex] = { ...row, columns: nextColumns };
            return {
              ...design,
              body: {
                ...design.body,
                rows
              }
            };
          }

          if (marker.type === 'content') {
            const contentIndex = contents.findIndex((item) => item?.id === marker.id);
            if (contentIndex >= 0) {
              const nextContents = [...contents];
              nextContents.splice(contentIndex + 1, 0, content);
              const nextColumns = [...columns];
              nextColumns[columnIndex] = { ...column, contents: nextContents };
              rows[rowIndex] = { ...row, columns: nextColumns };
              return {
                ...design,
                body: {
                  ...design.body,
                  rows
                }
              };
            }
          }
        }
      }
    }

    const firstRow = rows[0] || null;
    const firstColumns = Array.isArray(firstRow?.columns) ? [...firstRow.columns] : [];
    if (firstColumns.length > 0) {
      const firstColumn = firstColumns[0];
      const firstContents = Array.isArray(firstColumn?.contents) ? [...firstColumn.contents] : [];
      firstContents.push(content);
      firstColumns[0] = { ...firstColumn, contents: firstContents };
      rows[0] = { ...firstRow, columns: firstColumns };
      return {
        ...design,
        body: {
          ...design.body,
          rows
        }
      };
    }

    rows.push(createRow());
    return {
      ...design,
      body: {
        ...design.body,
        rows
      }
    };
  };

  const openUnlayerTemplateGallery = async () => {
    const editor = emailEditorRef.current?.editor;
    if (!editor) {
      alert('Builder is still loading. Please try again in a moment.');
      return;
    }
    setShowUnlayerPicker(true);
    if (unlayerTemplates.length > 0) return;
    setUnlayerPickerLoading(true);
    setUnlayerPickerError('');
    try {
      const curatedRes = await templatesAPI.getCuratedUnlayerTemplates();
      const curatedTemplates = curatedRes?.data?.data || [];
      if (Array.isArray(curatedTemplates) && curatedTemplates.length > 0) {
        setUnlayerTemplates(curatedTemplates);
        setUnlayerTemplateSource('curated');
      } else {
        const fallbackRes = await templatesAPI.getUnlayerTemplates();
        setUnlayerTemplates(fallbackRes?.data?.data || []);
        setUnlayerTemplateSource('unlayer');
      }
    } catch (err) {
      try {
        const fallbackRes = await templatesAPI.getUnlayerTemplates();
        setUnlayerTemplates(fallbackRes?.data?.data || []);
        setUnlayerTemplateSource('unlayer');
      } catch {
        setUnlayerPickerError('Failed to load starter templates. Please try again.');
      }
    } finally {
      setUnlayerPickerLoading(false);
    }
  };

  const handleApplyUnlayerTemplate = (template) => {
    const editor = emailEditorRef.current?.editor;
    if (!editor) return;
    const sourceDesign = template?.design;
    if (!sourceDesign?.body?.rows) {
      alert('This starter template is missing a valid Unlayer design.');
      return;
    }

    const normalizedDesign = normalizeDesign(sourceDesign);

    editor.loadDesign(normalizedDesign);
    setFormData((prev) => ({
      ...prev,
      body: '',
      _builderDesign: normalizedDesign,
      name: prev.name || template.name || '',
      subject: prev.subject || template.name || ''
    }));
    setBuilderHtml('');
    setBuilderVersion((value) => value + 1);

    setShowUnlayerPicker(false);
    setLibraryApplyBanner(`Template applied: ${template.name}`);
    setTimeout(() => setLibraryApplyBanner(''), 5000);
  };

  const formatTemplateApiError = (err, fallbackMessage) => {
    const responseData = err?.response?.data || {};
    const mainMessage = responseData.error || err?.message || fallbackMessage;
    const details = [];

    if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
      details.push(...responseData.errors.map((item) => `- ${item}`));
    }

    if (Array.isArray(responseData.details) && responseData.details.length > 0) {
      details.push(...responseData.details.map((item) => `- ${item}`));
    } else if (typeof responseData.details === 'string' && responseData.details.trim()) {
      details.push(`- ${responseData.details.trim()}`);
    }

    if (details.length === 0) {
      return mainMessage;
    }

    return `${mainMessage}\n\nDetails:\n${details.join('\n')}`;
  };

  const closeManagedImagePicker = () => {
    setShowManagedImagePicker(false);
    managedImageInsertCallbackRef.current = null;
  };

  const handleManagedImageSelected = (imageUrl) => {
    const callback = managedImageInsertCallbackRef.current;
    if (callback) {
      callback({ progress: 100, url: imageUrl || '' });
    }
    closeManagedImagePicker();
  };

  const handleBuilderReady = () => {
    setBuilderReady(true);

    const editor = emailEditorRef.current?.editor;
    if (!editor) return;

    editor.registerCallback('image', (file, done) => {
      // Always open the managed library picker.
      // Users can upload new images from within the picker.
      managedImageInsertCallbackRef.current = done;
      setShowManagedImagePicker(true);
    });

    editor.addEventListener('design:updated', (data) => {
      const type = typeof data?.type === 'string' ? data.type : '';
      const id = data?.item?.id;
      if (id) {
        lastEditedDesignItemRef.current = {
          id,
          type: type === 'row' || type === 'content' || type === 'column' ? type : 'content'
        };
      }
      syncBuilderToBody();
    });
    // When a user picks a pre-built Unlayer template, sync immediately so save/update
    // uses the selected template even before manual edits.
    editor.addEventListener('design:loaded', syncBuilderToBody);
    editor.addEventListener('template:loaded', syncBuilderToBody);

    if (pendingBuilderHtmlRef.current) {
      loadBuilderContent(pendingBuilderHtmlRef.current);
      pendingBuilderHtmlRef.current = '';
    } else if (formData.body) {
      loadBuilderContent(formData.body);
    }

    if (pendingOpenStarterRef.current) {
      pendingOpenStarterRef.current = false;
      openUnlayerTemplateGallery();
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [showAllTemplates, canViewAllTemplates]);

  useEffect(() => {
    if (!showForm || !builderReady) return;
    if (hasHydratedBuilderRef.current) return;
    if (!formData.body && !formData._builderDesign) return;
    if (formData.body === builderHtml && !formData._builderDesign) return;

    const editor = emailEditorRef.current?.editor;
    if (!editor) return;

    if (formData._builderDesign) {
      editor.loadDesign(normalizeDesign(formData._builderDesign));
      setBuilderHtml('');
    } else {
      loadBuilderContent(formData.body);
    }

    hasHydratedBuilderRef.current = true;
  }, [showForm, builderReady, formData.body, formData._builderDesign, builderHtml]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || editingId === editId) return;
    handleEdit(editId);
  }, [searchParams]);

  useEffect(() => {
    if (createTemplateInitRef.current) return;

    const shouldOpenNewTemplate = searchParams.get('new') === '1';
    if (!shouldOpenNewTemplate) return;

    createTemplateInitRef.current = true;

    const eventTitle = String(searchParams.get('eventTitle') || '').trim();
    const shouldOpenLegacyLibrary = searchParams.get('legacyLibrary') === '1';
    const shouldOpenStarterTemplates = searchParams.get('library') === '1' || searchParams.get('starters') === '1';

    setEditingId(null);
    setViewingTemplate(null);
    setShowForm(true);
    setFormData((prev) => ({
      ...prev,
      name: prev.name || (eventTitle ? `Event Invitation: ${eventTitle}` : prev.name),
      subject: prev.subject || (eventTitle ? `You're Invited: ${eventTitle}` : prev.subject)
    }));

    if (shouldOpenLegacyLibrary) {
      setShowTemplateLibrary(true);
    } else if (shouldOpenStarterTemplates) {
      pendingOpenStarterRef.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    const fromEvent = searchParams.get('fromEvent') === '1';
    if (!fromEvent) {
      setEventContext(null);
      return;
    }

    const eventId = searchParams.get('eventId');
    const eventTitle = String(searchParams.get('eventTitle') || '').trim();

    if (!eventId) {
      if (eventTitle) {
        setEventContext({ Title: eventTitle });
      }
      return;
    }

    const loadEventContext = async () => {
      try {
        const response = await api.get(`/events/${eventId}`);
        const event = response?.data?.event || null;

        if (!event) {
          setEventContext(eventTitle ? { Title: eventTitle } : null);
          return;
        }

        setEventContext({
          EventId: event.EventId,
          Title: event.Title || eventTitle || '',
          Description: event.Description || '',
          EventDate: event.EventDate || '',
          EventDateDisplay: formatEventDate(event.EventDate, event.EventTimeZone),
          EventTimeZone: event.EventTimeZone || '',
          Location: event.Location || '',
          VirtualLink: event.VirtualLink || '',
          EventContact: event.EventContactDisplay || event.EventContact || ''
        });
      } catch (error) {
        console.error('Failed to load event context for template builder', error);
        setEventContext(eventTitle ? { Title: eventTitle } : null);
      }
    };

    loadEventContext();
  }, [searchParams]);

  useEffect(() => {
    if (!showForm) {
      setBuilderReady(false);
      setBuilderHtml('');
      pendingBuilderHtmlRef.current = '';
      hasHydratedBuilderRef.current = false;
    }
  }, [showForm]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = canViewAllTemplates && showAllTemplates
        ? await api.get('/admin/templates')
        : await templatesAPI.getAll();
      setTemplates(response.data);
    } catch (err) {
      console.error('Failed to load templates', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = await exportBuilderHtml();
      
      const payload = {
        ...formData,
        body
      };
      
      if (formData._builderDesign) {
        payload._builderDesign = formData._builderDesign;
      }

      const fromEvent = searchParams.get('fromEvent') === '1';
      const eventId = eventContext?.EventId || searchParams.get('eventId');
      let savedTemplateId = editingId || null;

      if (editingId) {
        const updateResponse = await templatesAPI.update(editingId, payload);
        savedTemplateId = updateResponse?.data?.TemplateId || editingId;
      } else {
        const createResponse = await templatesAPI.create(payload);
        savedTemplateId = createResponse?.data?.TemplateId || null;
      }

      if (fromEvent && eventId && savedTemplateId) {
        await api.post(`/events/${eventId}/link-template`, { templateId: savedTemplateId });
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', subject: '', body: '', description: '', isGlobal: false });
      await loadTemplates();

      if (fromEvent && eventId) {
        navigate(`/events/${eventId}`);
      }
    } catch (err) {
      alert(formatTemplateApiError(err, `Failed to ${editingId ? 'update' : 'create'} template`));
    }
  };

  const handleSaveAsNewTemplate = async () => {
    try {
      const body = await exportBuilderHtml();
      const payload = {
        ...formData,
        body
      };
      
      if (formData._builderDesign) {
        payload._builderDesign = formData._builderDesign;
      }

      const fromEvent = searchParams.get('fromEvent') === '1';
      const eventId = eventContext?.EventId || searchParams.get('eventId');

      const createResponse = await templatesAPI.create(payload);
      const createdTemplateId = createResponse?.data?.TemplateId || null;

      if (fromEvent && eventId && createdTemplateId) {
        await api.post(`/events/${eventId}/link-template`, { templateId: createdTemplateId });
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', subject: '', body: '', description: '', isGlobal: false, _builderDesign: null });
      await loadTemplates();

      if (fromEvent && eventId) {
        navigate(`/events/${eventId}`);
      }
    } catch (err) {
      alert(formatTemplateApiError(err, 'Failed to save as new template'));
    }
  };

  const handleView = async (id) => {
    try {
      const response = await templatesAPI.getOne(id);
      setViewingTemplate(response.data);
    } catch (err) {
      alert('Failed to load template details');
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await templatesAPI.getOne(id);
      const template = response.data;
      const builderDesign = template.BodyDesign ? normalizeDesign(JSON.parse(template.BodyDesign)) : null;
      hasHydratedBuilderRef.current = false;
      setFormData({
        name: template.Name,
        subject: template.Subject,
        body: template.Body,
        description: template.Description || '',
        isGlobal: template.IsGlobal || false,
        _builderDesign: builderDesign
      });
      setEditingId(id);
      setShowForm(true);
      setViewingTemplate(null);
    } catch (err) {
      alert('Failed to load template for editing');
    }
  };

  const handleCancelEdit = () => {
    hasHydratedBuilderRef.current = false;
    setShowForm(false);
    setEditingId(null);
    setLibraryApplyBanner('');
    setEventContext(null);
    setFormData({ name: '', subject: '', body: '', description: '', isGlobal: false, _builderDesign: null });
  };

  const handleCreateNewTemplate = () => {
    // Clear any event context and form data when creating a new template from the list
    setEventContext(null);
    setFormData({ name: '', subject: '', body: '', description: '', isGlobal: false, _builderDesign: null });
    setShowForm(true);
  };

  const handleDelete = async () => {
    try {
      await templatesAPI.delete(confirmDelete.id);
      await loadTemplates();
      setConfirmDelete({ show: false, id: null });
    } catch (err) {
      alert('Failed to delete template');
      setConfirmDelete({ show: false, id: null });
    }
  };

  const handleOpenAIAssist = () => {
    // Pre-fill AI form with existing template data
    const purpose = formData.name || 'New template';
    const eventDetailBlock = eventContext
      ? [
        eventContext.Description,
        eventContext.EventDateDisplay ? `Date & Time: ${eventContext.EventDateDisplay}` : '',
        eventContext.Location ? `Location: ${eventContext.Location}` : '',
        eventContext.VirtualLink ? `Virtual Link: ${eventContext.VirtualLink}` : ''
      ].filter(Boolean).join('\n')
      : '';
    const details = [formData.description || '', eventDetailBlock].filter(Boolean).join('\n\n');
    
    setAiFormData({
      purpose,
      tone: 'professional',
      type: 'event-invite',
      details,
      designStyle: 'modern-corporate',
      layoutStyle: 'hero-cta',
      creativityLevel: 'balanced',
      usePexelsImages: false,
      styleProfile: null
    });
    setIgnoreTemplateContext(false);
    setShowAIAssist(true);
  };

  const updateAiField = (key, value) => {
    setAiFormData((prev) => ({
      ...prev,
      [key]: value,
      // If user manually changes core visual settings, clear inherited style profile.
      ...(key === 'type' || key === 'designStyle' || key === 'layoutStyle' ? { styleProfile: null } : {})
    }));
  };

  // Email type configurations
  const emailTypeConfigs = {
    'event-invite': {
      label: 'Event Invitation',
      description: 'Professional event invitations with RSVP calls-to-action',
      purposeLabel: 'Event Purpose',
      purposePlaceholder: 'e.g., Annual tech networking mixer',
      detailsPlaceholder: 'Add specific details like date, time, location, speakers, etc.'
    },
    'newsletter': {
      label: 'Newsletter',
      description: 'Monthly or regular newsletters featuring news and updates',
      purposeLabel: 'Newsletter Topic/Theme',
      purposePlaceholder: 'e.g., Monthly industry insights & news',
      detailsPlaceholder: 'Add main topics, featured articles, or story ideas'
    },
    'promotional': {
      label: 'Promotional Campaign',
      description: 'Sales and promotional campaigns with special offers',
      purposeLabel: 'Campaign Goal',
      purposePlaceholder: 'e.g., Spring sale promotion for premium features',
      detailsPlaceholder: 'Add offer details, pricing, discount percentage, or special terms'
    },
    'product-update': {
      label: 'Product Update',
      description: 'Announce new features and product improvements',
      purposeLabel: 'Update Focus',
      purposePlaceholder: 'e.g., Launch of new analytics dashboard',
      detailsPlaceholder: 'Add new features, improvements, or technical details'
    },
    'announcement': {
      label: 'Announcement',
      description: 'Important company announcements and updates',
      purposeLabel: 'Announcement Type',
      purposePlaceholder: 'e.g., New office location, policy changes',
      detailsPlaceholder: 'Add key details and what it means for recipients'
    }
  };

  const handleAIAssist = async (e) => {
    e.preventDefault();
    setAiLoading(true);

    try {
      const contextData = ignoreTemplateContext ? { event: eventContext || null } : {
        title: formData.name,
        description: formData.description,
        subject: formData.subject,
        event: eventContext || null
      };

      const response = await templatesAPI.aiDesign({
        purpose: aiFormData.purpose,
        tone: aiFormData.tone,
        details: aiFormData.details,
        type: aiFormData.type,
        designStyle: aiFormData.designStyle,
        layoutStyle: aiFormData.layoutStyle,
        creativityLevel: aiFormData.creativityLevel,
        enforcePlaceholderImages: true,
        usePexelsImages: false,
        styleProfile: aiFormData.styleProfile,
        templateName: ignoreTemplateContext ? `${emailTypeConfigs[aiFormData.type].label} Template` : (formData.name || `${emailTypeConfigs[aiFormData.type].label} Template`),
        context: contextData
      });

      const { copy, design } = response.data;

      if (!design || !design?.body?.rows) {
        throw new Error('AI did not return a valid Unlayer design payload');
      }

      // Persist generated output to body and subject so Builder/HTML stay in sync
      setFormData(prev => ({
        ...prev,
        subject: copy.subject,
        name: prev.name || (copy.html ? 'AI-Generated Template' : ''),
        body: copy.html || prev.body,
        _builderDesign: design
      }));

      emailEditorRef.current?.editor?.loadDesign(normalizeDesign(design));
      if (!emailEditorRef.current?.editor) {
        loadBuilderContent(JSON.stringify(normalizeDesign(design)));
      }
      setBuilderHtml(copy.html || '');

      // Close AI modal
      setShowAIAssist(false);
      setAiFormData({
        purpose: '',
        tone: 'professional',
        type: 'event-invite',
        details: '',
        designStyle: 'modern-corporate',
        layoutStyle: 'hero-cta',
        creativityLevel: 'balanced',
        usePexelsImages: false,
        styleProfile: null
      });

    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to generate AI template design';
      const details = err.response?.data?.details;
      const hint = err.response?.data?.hint;
      
      let fullMessage = errorMsg;
      if (details) {
        fullMessage += `\n\nDetails: ${details}`;
      }
      if (hint) {
        fullMessage += `\n\nHint: ${hint}`;
      }
      
      alert(fullMessage);
    } finally {
      setAiLoading(false);
    }
  };

  const handleTemplateLibrarySelected = (template) => {
    setSelectedLibraryTemplate(template);
    setShowTemplateLibrary(false);
    setShowTemplateCustomizer(true);
    // Open the form if not already open
    if (!showForm) {
      setShowForm(true);
    }
  };

  const handleTemplateCustomizerApplied = (result) => {
    const { html, templateName, subject, design } = result;

    // Force selected template into Builder so replacement is visible immediately.
    if (design && emailEditorRef.current?.editor) {
      emailEditorRef.current.editor.loadDesign(normalizeDesign(design));
      setBuilderHtml('');
    } else if (html) {
      setBuilderHtml(html);
      loadBuilderContent(html);
    }

    // Pre-fill template name if empty
    setFormData(prev => ({
      ...prev,
      body: html,
      _builderDesign: normalizeDesign(design || prev._builderDesign || null),
      name: prev.name || templateName || 'My Template',
      subject: subject || prev.subject || (eventContext?.Title ? `You're Invited: ${eventContext.Title}` : prev.subject)
    }));

    setLibraryApplyBanner(`Template replaced from library: ${templateName || 'Selected template'}`);
    setShowTemplateCustomizer(false);
    setSelectedLibraryTemplate(null);
  };

  useEffect(() => {
    if (!libraryApplyBanner) return;
    const timeout = setTimeout(() => setLibraryApplyBanner(''), 5000);
    return () => clearTimeout(timeout);
  }, [libraryApplyBanner]);

  const handleUseTemplateStyleInAI = (template) => {
    if (!template) return;

    const preset = getAiPresetForTemplate(template);

    setAiFormData((prev) => ({
      ...prev,
      type: preset.type,
      designStyle: preset.designStyle,
      layoutStyle: preset.layoutStyle,
      styleProfile: preset.styleProfile || null
    }));

    if (!showForm) {
      setShowForm(true);
    }

    setShowTemplateLibrary(false);
    setShowTemplateCustomizer(false);
    setShowAIAssist(true);
  };

  const handleInsertRscLogo = async (logoUrl) => {
    const editor = emailEditorRef.current?.editor;
    if (!editor) {
      alert('Open the Builder tab first to insert a logo.');
      return;
    }

    const design = await new Promise((resolve) => {
      editor.saveDesign((savedDesign) => resolve(savedDesign || null));
    });

    if (!design?.body) {
      alert('Could not access current builder design. Please try again.');
      return;
    }

    const rows = Array.isArray(design.body.rows) ? design.body.rows : [];
    const existingLogo = rows.some((row) =>
      (row?.columns || []).some((column) =>
        (column?.contents || []).some((content) =>
          content?.type === 'image' && content?.values?.src?.url === logoUrl
        )
      )
    );
    if (existingLogo) return;

    const logoContent = {
      id: `logo-image-${Date.now()}`,
      type: 'image',
      values: {
        src: {
          url: logoUrl,
          width: RSC_LOGO_DEFAULT_WIDTH,
          height: null
        },
        altText: 'RSC Logo',
        textAlign: 'left',
        containerPadding: '8px 0px 12px',
        width: `${RSC_LOGO_DEFAULT_WIDTH}px`
      }
    };

    const nextDesign = insertContentIntoDesign(design, logoContent, 'logo');

    editor.loadDesign(nextDesign);
    setBuilderVersion((value) => value + 1);
    await syncBuilderToBody();
  };

  const handleInsertRsvpButton = async () => {
    const editor = emailEditorRef.current?.editor;
    if (!editor) {
      alert('Open the Builder tab first to insert RSVP button.');
      return;
    }

    const design = await new Promise((resolve) => {
      editor.saveDesign((savedDesign) => resolve(savedDesign || null));
    });

    if (!design?.body) {
      alert('Could not access current builder design. Please try again.');
      return;
    }

    const rows = Array.isArray(design.body.rows) ? design.body.rows : [];
    const hasButtonWithHref = (hrefToken) => rows.some((row) =>
      (row?.columns || []).some((column) =>
        (column?.contents || []).some((content) =>
          content?.type === 'button' && String(content?.values?.href || '').includes(hrefToken)
        )
      )
    );

    const existingAcceptButton = hasButtonWithHref('{{acceptLink}}');
    const existingDeclineButton = hasButtonWithHref('{{declineLink}}');
    if (existingAcceptButton && existingDeclineButton) return;

    const buildRsvpButton = ({ idPrefix, text, href, backgroundColor }) => ({
      id: `${idPrefix}-${Date.now()}`,
      type: 'button',
      values: {
        text,
        href,
        textAlign: 'left',
        containerPadding: '8px 0px 12px',
        buttonColors: {
          color: '#ffffff',
          backgroundColor
        },
        size: {
          autoWidth: true,
          width: 'auto'
        },
        border: {
          radius: 6
        }
      }
    });

    let nextDesign = design;

    if (!existingAcceptButton) {
      nextDesign = insertContentIntoDesign(
        nextDesign,
        buildRsvpButton({
          idPrefix: 'rsvp-button',
          text: "Yes, I'll attend",
          href: '{{acceptLink}}',
          backgroundColor: '#0078d4'
        }),
        'rsvp'
      );
    }

    if (!existingDeclineButton) {
      nextDesign = insertContentIntoDesign(
        nextDesign,
        buildRsvpButton({
          idPrefix: 'decline-button',
          text: "No, I can't make it",
          href: '{{declineLink}}',
          backgroundColor: '#6b7280'
        }),
        'decline'
      );
    }

    editor.loadDesign(nextDesign);
    setBuilderVersion((value) => value + 1);
    await syncBuilderToBody();
  };

  const isEventTemplate = Boolean(eventContext?.Title)
    || /event|invite|invitation|rsvp/i.test(`${formData.name || ''} ${formData.subject || ''}`);

  if (loading) return <div className="loading">Loading templates...</div>;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Email Templates</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {canViewAllTemplates && (
            <button
              onClick={() => setShowAllTemplates(prev => !prev)}
              className="btn btn-secondary"
            >
              {showAllTemplates ? 'Show My Templates' : 'Show All Templates'}
            </button>
          )}
          <button onClick={() => (showForm ? handleCancelEdit() : handleCreateNewTemplate())} className="btn btn-primary">
            {showForm ? 'Cancel' : 'Create Template'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="card">
          <h2>{editingId ? 'Edit Template' : 'New Template'}</h2>
          {libraryApplyBanner && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              background: '#e6ffed',
              border: '1px solid #34d399',
              borderRadius: '6px',
              padding: '9px 12px',
              marginBottom: '12px',
              fontSize: '13px',
              color: '#065f46'
            }}>
              <span>{libraryApplyBanner}</span>
              <button
                type="button"
                onClick={() => setLibraryApplyBanner('')}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#065f46',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '14px'
                }}
                aria-label="Dismiss banner"
              >
                ×
              </button>
            </div>
          )}
          {eventContext?.Title && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#e8f4fd', border: '1px solid #bee3f8',
              borderRadius: '6px', padding: '8px 12px', marginBottom: '16px',
              fontSize: '14px', color: '#2c5282'
            }}>
              <span>🗓️</span>
              <span>
                <strong>Event:</strong> {eventContext.Title}
                {eventContext.EventDateDisplay && <span style={{ marginLeft: '12px', color: '#4a6fa5' }}>{eventContext.EventDateDisplay}</span>}
                {eventContext.Location && <span style={{ marginLeft: '12px', color: '#4a6fa5' }}>📍 {eventContext.Location}</span>}
              </span>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Template Name *"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Subject Line *"
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontWeight: 'bold' }}>Email Body *</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowTemplateLibrary(true)}
                    className="btn"
                    style={{ 
                      padding: '6px 12px', 
                      fontSize: '12px',
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Open legacy MJML-based template library"
                  >
                    Legacy MJML Library
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenAIAssist}
                    className="btn"
                    style={{ 
                      padding: '6px 12px', 
                      fontSize: '14px',
                      backgroundColor: '#6f42c1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✨ AI Assist
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setShowImageLibrary(true);
                      if (imageLibraryImages.length > 0) return;
                      setImageLibraryLoading(true);
                      try {
                        const res = await templatesAPI.listDesignImages();
                        const imgs = res.data?.images || [];
                        setImageLibraryImages(imgs);
                        const variantResults = await Promise.all(
                          imgs.map((img) =>
                            templatesAPI.listDesignImageVariants(img.ImageId)
                              .then((r) => ({ id: img.ImageId, variants: r.data?.variants || [] }))
                              .catch(() => ({ id: img.ImageId, variants: [] }))
                          )
                        );
                        const variantMap = {};
                        variantResults.forEach(({ id, variants }) => { variantMap[id] = variants; });
                        setImageLibraryVariants(variantMap);
                      } catch {
                        setImageLibraryImages([]);
                      } finally {
                        setImageLibraryLoading(false);
                      }
                    }}
                    className="btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '14px',
                      backgroundColor: '#0369a1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="View CDN image library"
                  >
                    🖼 Image Library
                  </button>
                  <button
                    type="button"
                    onClick={openUnlayerTemplateGallery}
                    className="btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '14px',
                      backgroundColor: '#475569',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Open curated Unlayer-native starter templates"
                  >
                    Starter Templates
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsertRscLogo(RSC_LOGO_WHITE_URL)}
                    className="btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '14px',
                      backgroundColor: '#334155',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Insert white RSC logo"
                  >
                    RSC Logo (White)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsertRscLogo(RSC_LOGO_BLUE_URL)}
                    className="btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '14px',
                      backgroundColor: '#0f766e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Insert blue RSC logo"
                  >
                    RSC Logo (Blue)
                  </button>
                  {isEventTemplate && (
                    <button
                      type="button"
                      onClick={handleInsertRsvpButton}
                      className="btn"
                      style={{
                        padding: '6px 12px',
                        fontSize: '14px',
                        backgroundColor: '#1d4ed8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Insert RSVP button"
                    >
                      Add RSVP Button
                    </button>
                  )}
                </div>
              </div>

              <div className="template-builder">
                  <EmailEditor
                    ref={emailEditorRef}
                    minHeight="700px"
                    style={{ flex: 1 }}
                    onReady={handleBuilderReady}
                    options={{
                      displayMode: 'email',
                      apiKey: import.meta.env.VITE_UNLAYER_API_KEY,
                      projectId: Number(import.meta.env.VITE_UNLAYER_PROJECT_ID),
                      tools: {
                        html: { enabled: true }
                      },
                      appearance: {
                        panels: {
                          tools: {
                            dock: 'right'
                          }
                        }
                      }
                    }}
                  />
              </div>

              {/* Template Library Modal */}
              {showTemplateLibrary && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '20px'
                }}>
                  <div style={{ width: '100%', maxWidth: '900px', maxHeight: '90vh', position: 'relative' }}>
                    <button
                      type="button"
                      aria-label="Close template library"
                      onClick={() => setShowTemplateLibrary(false)}
                      style={{
                        position: 'absolute',
                        top: '-14px',
                        right: '-14px',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: 'none',
                        background: '#1f2d3d',
                        color: '#fff',
                        fontSize: '20px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
                        zIndex: 1002
                      }}
                    >
                      ×
                    </button>
                    <TemplateLibrary
                      onTemplateSelected={handleTemplateLibrarySelected}
                      onUseStyleInAI={handleUseTemplateStyleInAI}
                      onClose={() => setShowTemplateLibrary(false)}
                    />
                  </div>
                </div>
              )}

              {/* Template Customizer Modal */}
              {showTemplateCustomizer && selectedLibraryTemplate && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '20px'
                }}>
                  <div style={{ width: '100%', maxWidth: '1100px', maxHeight: '90vh' }}>
                    <TemplateCustomizer
                      template={selectedLibraryTemplate}
                      eventContext={eventContext}
                      onApply={handleTemplateCustomizerApplied}
                      onUseStyleInAI={handleUseTemplateStyleInAI}
                      onBack={() => {
                        setShowTemplateCustomizer(false);
                        setShowTemplateLibrary(true);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Unlayer Template Picker Modal */}
              {showUnlayerPicker && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.6)', zIndex: 1100,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '20px'
                }}>
                  <div style={{
                    background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '960px',
                    maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '18px 24px', borderBottom: '1px solid #e5e7eb'
                    }}>
                      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>Starter Templates (Unlayer-native)</h2>
                      <button
                        type="button"
                        onClick={() => setShowUnlayerPicker(false)}
                        style={{
                          background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer',
                          color: '#64748b', lineHeight: 1, padding: '0 4px'
                        }}
                        aria-label="Close"
                      >×</button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                      {!unlayerPickerLoading && !unlayerPickerError && unlayerTemplates.length > 0 && (
                        <div style={{ marginBottom: '12px', color: '#475569', fontSize: '13px' }}>
                          Source: {unlayerTemplateSource === 'curated' ? 'Curated starters' : 'Unlayer project templates'}
                        </div>
                      )}
                      {unlayerPickerLoading && (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>Loading templates…</div>
                      )}
                      {unlayerPickerError && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#dc2626' }}>{unlayerPickerError}</div>
                      )}
                      {!unlayerPickerLoading && !unlayerPickerError && unlayerTemplates.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                          No templates found in your Unlayer project.
                        </div>
                      )}
                      {!unlayerPickerLoading && !unlayerPickerError && unlayerTemplates.length > 0 && (
                        <div style={{
                          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px'
                        }}>
                          {unlayerTemplates.map((tpl) => (
                            <div
                              key={tpl.id}
                              onClick={() => handleApplyUnlayerTemplate(tpl)}
                              style={{
                                border: '2px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden',
                                cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s'
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.borderColor = '#3b82f6';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.2)';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              {tpl.thumbnail ? (
                                <img
                                  src={tpl.thumbnail}
                                  alt={tpl.name}
                                  onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src = TEMPLATE_IMAGE_FALLBACK_URL;
                                  }}
                                  style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }}
                                />
                              ) : (
                                <div style={{
                                  width: '100%', height: '150px', background: '#f1f5f9',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8'
                                }}>No preview</div>
                              )}
                              <div style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>
                                {tpl.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
            <textarea
              placeholder="Description (optional)"
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <div style={{ textAlign: 'left', margin: '15px 0 10px 0' }}>
              <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={formData.isGlobal}
                  onChange={(e) => setFormData({ ...formData, isGlobal: e.target.checked })}
                />
                <span>Make this template global (visible to all users)</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>

              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update Template' : 'Create Template'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={handleSaveAsNewTemplate}
                  className="btn"
                  style={{ backgroundColor: '#17a2b8', color: 'white' }}
                >
                  Save as New Template
                </button>
              )}
              {editingId && (
                <button type="button" onClick={handleCancelEdit} className="btn" style={{ backgroundColor: '#6c757d', color: 'white' }}>
                  Cancel
                </button>
              )}
            </div>
          </form>
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h3 style={{ marginBottom: '10px' }}>Available Placeholders:</h3>
            <code>{'{{FirstName}}, {{LastName}}, {{Email}}, {{Company}}, {{JobTitle}}'}</code>
          </div>
        </div>
      )}

      {showImageLibrary && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.7)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowImageLibrary(false); }}
        >
          <div
            style={{
              width: 'min(1100px, 96vw)',
              maxHeight: '90vh',
              overflow: 'auto',
              background: '#f8fafc',
              borderRadius: '14px',
              boxShadow: '0 30px 70px rgba(0,0,0,0.35)',
              padding: '24px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', color: '#0f172a' }}>Image Library</h2>
                <p style={{ margin: '6px 0 0', color: '#475569', fontSize: '14px' }}>Copy a CDN URL and paste it into any Unlayer image block.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowImageLibrary(false)}
                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
              >
                Close
              </button>
            </div>

            {imageLibraryLoading ? (
              <p style={{ color: '#475569' }}>Loading images...</p>
            ) : imageLibraryImages.length === 0 ? (
              <p style={{ color: '#475569' }}>No images in the library yet. Go to the Template Images page to upload some.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
                {imageLibraryImages.flatMap((img) => {
                  const variants = imageLibraryVariants[img.ImageId] || [];
                  return [
                    {
                      key: `orig-${img.ImageId}`,
                      imageId: img.ImageId,
                      name: img.Name,
                      kind: 'Original',
                      description: `${img.Width || '?'} x ${img.Height || '?'}px`,
                      url: img.OriginalUrl,
                      copiedKey: `orig-${img.ImageId}`
                    },
                    ...variants.map((v) => ({
                      key: `var-${v.VariantId}`,
                      imageId: img.ImageId,
                      name: img.Name,
                      kind: 'Variant',
                      description: `${v.Mode} ${v.TargetWidth || 'auto'}x${v.TargetHeight || 'auto'} .${v.OutputFormat}`,
                      url: v.VariantUrl,
                      copiedKey: `v-${v.VariantId}`
                    }))
                  ];
                }).map((item) => (
                  <div
                    key={item.key}
                    style={{ background: '#fff', border: '1px solid #dbe3ef', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}
                  >
                    <img
                      src={item.url}
                      alt={`${item.name} ${item.kind}`}
                      style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', background: '#e2e8f0' }}
                    />
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      {item.kind}: {item.description}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', borderRadius: '6px', padding: '6px 8px', border: '1px solid #e2e8f0' }}>
                      <span style={{ flex: 1, fontSize: '11px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.url}>
                        {item.kind} URL
                      </span>
                      <button
                        type="button"
                        title="Copy URL"
                        onClick={() => {
                          navigator.clipboard.writeText(item.url).then(() => {
                            setImageLibraryCopied(item.copiedKey);
                            setTimeout(() => setImageLibraryCopied(null), 2000);
                          });
                        }}
                        style={{
                          flexShrink: 0,
                          padding: '4px 8px',
                          borderRadius: '5px',
                          border: '1px solid #cbd5e1',
                          background: imageLibraryCopied === item.copiedKey ? '#dcfce7' : '#fff',
                          cursor: 'pointer',
                          fontSize: '14px',
                          lineHeight: 1,
                          transition: 'background 0.2s'
                        }}
                      >
                        {imageLibraryCopied === item.copiedKey ? '✓' : 'Copy'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showManagedImagePicker && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.7)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div
            style={{
              width: 'min(1240px, 96vw)',
              maxHeight: '92vh',
              overflow: 'auto',
              background: '#f8fafc',
              borderRadius: '14px',
              boxShadow: '0 30px 70px rgba(0,0,0,0.35)',
              padding: '20px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', color: '#0f172a' }}>Insert Managed Image</h2>
                <p style={{ margin: '6px 0 0', color: '#475569' }}>Choose an original asset or generate a transformed variant for this design.</p>
              </div>
              <button
                type="button"
                onClick={closeManagedImagePicker}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Close
              </button>
            </div>
            <TemplateImages embedded onSelectImage={handleManagedImageSelected} />
          </div>
        </div>
      )}

      <div className="card">
        <h2>Template Library ({templates.length})</h2>
        {templates.length === 0 ? (
          <p>No templates found. Create your first template to get started.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                {showAllTemplates && canViewAllTemplates && <th>Owner</th>}
                <th>Subject</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.TemplateId}>
                  <td>
                    <strong>{template.Name}</strong>
                    {template.IsGlobal && (
                      <span
                        style={{
                          marginLeft: '10px',
                          fontSize: '16px'
                        }}
                        title="Global"
                      >🌐</span>
                    )}
                  </td>
                  {showAllTemplates && canViewAllTemplates && (
                    <td>{template.OwnerDisplayName || template.OwnerEmail || template.UserId}</td>
                  )}
                  <td>{template.Subject}</td>
                  <td>{template.Description}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => handleView(template.TemplateId)}
                        title="View template"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '4px'
                        }}
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => handleEdit(template.TemplateId)}
                        title="Edit template"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '4px'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => setConfirmDelete({ show: true, id: template.TemplateId })}
                        title="Delete template"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '4px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {viewingTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: 0
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Template Details</h2>
              <button
                onClick={() => setViewingTemplate(null)}
                className="btn"
                style={{ backgroundColor: '#6c757d', color: 'white' }}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Name:</strong>
              <p style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {viewingTemplate.Name}
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Subject:</strong>
              <p style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {viewingTemplate.Subject}
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Body:</strong>
              <div style={{
                marginTop: '5px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}
              dangerouslySetInnerHTML={{ __html: viewingTemplate.Body }}
              />
            </div>

            {viewingTemplate.Description && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Description:</strong>
                <p style={{ marginTop: '5px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {viewingTemplate.Description}
                </p>
              </div>
            )}

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  handleEdit(viewingTemplate.TemplateId);
                }}
                className="btn"
                style={{ backgroundColor: '#ffc107', color: '#000' }}
              >
                Edit Template
              </button>
              <button
                onClick={() => setViewingTemplate(null)}
                className="btn"
                style={{ backgroundColor: '#6c757d', color: 'white' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmDelete.show}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ show: false, id: null })}
        confirmText="Delete"
        confirmStyle="danger"
      />

      {showAIAssist && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '20px' }}>✨ AI Template Assistant</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              <strong>{emailTypeConfigs[aiFormData.type].label}:</strong> {emailTypeConfigs[aiFormData.type].description}. Placeholder tags like {'{{'} FirstName {'}}'}  will be included automatically.
            </p>
            <p style={{ marginBottom: '20px', fontSize: '13px', color: '#888', fontStyle: 'italic' }}>
              AI generates Unlayer-native layouts with placeholder images so every section stays editable.
            </p>

            {/* Show existing template data if available */}
            {!ignoreTemplateContext && (formData.name || formData.description || formData.subject) && (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 'bold', color: '#333' }}>📋 Using Existing Template Data:</div>
                  <button
                    type="button"
                    onClick={() => { setIgnoreTemplateContext(true); setAiFormData(prev => ({ ...prev, purpose: '' })); }}
                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '12px', padding: '2px 6px', textDecoration: 'underline' }}
                  >Start Fresh</button>
                </div>
                {formData.name && (
                  <div style={{ fontSize: '14px', color: '#555', marginBottom: '5px' }}>
                    <strong>Template Name:</strong> {formData.name}
                  </div>
                )}
                {formData.subject && (
                  <div style={{ fontSize: '14px', color: '#555', marginBottom: '5px' }}>
                    <strong>Subject:</strong> {formData.subject}
                  </div>
                )}
                {formData.description && (
                  <div style={{ fontSize: '14px', color: '#555' }}>
                    <strong>Description:</strong> {formData.description}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px', fontStyle: 'italic' }}>
                  AI will enhance this information with professional copy
                </div>
              </div>
            )}
            
            <form onSubmit={handleAIAssist}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Email Type *
                </label>
                <select
                  value={aiFormData.type}
                  onChange={(e) => updateAiField('type', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="event-invite">Event Invitation</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="promotional">Promotional Campaign</option>
                  <option value="product-update">Product Update</option>
                  <option value="announcement">Announcement</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  {formData.name ? 'Additional Goal/Focus (optional)' : emailTypeConfigs[aiFormData.type].purposeLabel + ' *'}
                </label>
                <input
                  type="text"
                  placeholder={formData.name ? `e.g., Focus on ${emailTypeConfigs[aiFormData.type].label.toLowerCase()}` : emailTypeConfigs[aiFormData.type].purposePlaceholder}
                  required={!formData.name}
                  value={aiFormData.purpose}
                  onChange={(e) => setAiFormData({ ...aiFormData, purpose: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Tone
                </label>
                <select
                  value={aiFormData.tone}
                  onChange={(e) => updateAiField('tone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="urgent">Urgent</option>
                  <option value="casual">Casual</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Design Style
                </label>
                <select
                  value={aiFormData.designStyle}
                  onChange={(e) => updateAiField('designStyle', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="modern-corporate">Modern Corporate</option>
                  <option value="minimal-clean">Minimal Clean</option>
                  <option value="bold-promotional">Bold Promotional</option>
                  <option value="editorial-premium">Editorial Premium</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Layout Style
                </label>
                <select
                  value={aiFormData.layoutStyle}
                  onChange={(e) => updateAiField('layoutStyle', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="hero-cta">Hero + CTA</option>
                  <option value="two-column">Two Column</option>
                  <option value="single-column-story">Single Column Story</option>
                  <option value="card-sections">Card Sections</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Creativity Level
                </label>
                <select
                  value={aiFormData.creativityLevel}
                  onChange={(e) => updateAiField('creativityLevel', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="balanced">Balanced professional</option>
                  <option value="high-impact">High-impact creative</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Image Policy
                </label>
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#6c757d' }}>
                  Placeholder-only mode is enabled to keep generated layouts deterministic and fully editable.
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Additional Details (optional)
                </label>
                <textarea
                  placeholder={emailTypeConfigs[aiFormData.type].detailsPlaceholder}
                  rows="4"
                  value={aiFormData.details}
                  onChange={(e) => setAiFormData({ ...aiFormData, details: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAIAssist(false);
                    setAiFormData({
                      purpose: '',
                      tone: 'professional',
                      type: 'event-invite',
                      details: '',
                      designStyle: 'modern-corporate',
                      layoutStyle: 'hero-cta',
                      creativityLevel: 'balanced',
                      usePexelsImages: false,
                      styleProfile: null
                    });
                  }}
                  disabled={aiLoading}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: aiLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={aiLoading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6f42c1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: aiLoading ? 'not-allowed' : 'pointer',
                    opacity: aiLoading ? 0.6 : 1
                  }}
                >
                  {aiLoading ? 'Generating...' : 'Generate Copy + Layout'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const TemplatesWithProtection = () => (
  <ProtectedRoute requiredTab="templates">
    <Templates />
  </ProtectedRoute>
);

export default TemplatesWithProtection;
